<template>
  <div class="projects-wrapper" :class="$config.project">
    <div class="hero-section">
      <div class="container">
        <h1 class="h3">
          {{ $config.platform === 'fa' ? 'Financial Analysis Projects' : 'Data Science Projects' }}
        </h1>
        <p class="description col col-7">
          {{ $c.text('pages.projects.library.description') }}
        </p>
      </div>
    </div>
    <div class="content container">
      <div class="row">
        <div v-if="$config.project === 'lms'" class="types-tabs col col-12">
          <div class="tabs">
            <li v-for="tab in tabs" :key="tab.name" :class="{ active: currentTab === tab.name }">
              <a @click="changeTab(tab.name)">
                {{ tab.text }}
              </a>
            </li>
          </div>
        </div>
        <div class="main col col-9 md-8">
          <div class="search">
            <div class="search-holder">
              <SearchBar
                placeholder="Search projects"
                @search="search"
              />
              <Filters
                :filters="filters"
                applied-counter
                :reset="filtersReset"
                :de-selected="deSelected"
                class="mobile-filter"
                @change="filtrate"
              />
            </div>
            <SearchResults
              key="searchLabels"
              :filters="filters"
              :labels="searchLabels"
              @closeLabel="close"
              @clearAll="reset"
            />
          </div>
          <div class="row results">
            <Card
              v-for="(project, id) in projects.list" :key="id"
              :project="project"
              heading="h2"
              class="col-4 sm-6 md-6"
            />
          </div>
          <Pagination
            v-if="projects.count > 12"
            :page="page"
            :total="projects.count"
            :per-page="12"
            align="center"
            :links="false"
            @pagechanged="setPage"
          />
        </div>
        <aside class="col col-3 md-4">
          <FiltersAccordion
            :filters="filters"
            :reset="filtersReset"
            :de-selected="deSelected"
            class="desktop-filter"
            :class="{ 'lms-filters-accordion': $config.project === 'lms' }"
            @change="filtrate"
          />
        </aside>
      </div>
    </div>
    <section class="faq">
      <FrequentlyAskedQuestions group="projects" :results="data.faq" />
    </section>
  </div>
</template>

<script>
import FrequentlyAskedQuestions from '../FrequentlyAskedQuestions';
import FiltersAccordion from '../Courses/FiltersAccordion';
import SearchResults from '../Business/SearchResults';
import Filters from '../components/Filters';
import Pagination from '../components/Pagination';
import SearchBar from '../Business/SearchBar';
import Card from './Card';

export default {
  components: {
    FrequentlyAskedQuestions,
    FiltersAccordion,
    SearchResults,
    Filters,
    Pagination,
    Card,
    SearchBar,
  },
  props: {
    data: { type: Object, required: true, default: () => ({}) },
  },
  async asyncData({ $axios }) {
    const faq = await $axios.$get('/faq/groups?group[]=projects&group[]=project1').catch(() => false);

    return { faq };
  },
  data() {
    return {
      page: 1,
      projects: this.data.projects,
      paidAccess: this.data.paidAccess,
      keyword: '',
      filter: '',
      searchLabels: {
        results: this.data.projects.count,
        searchData: '',
        filtersData: {},
      },
      deSelectedOption: {},
      resetFilters: false,
      currentTab: 'all',
      tabs: [
        { name: 'all', text: 'All projects' },
        {
          name: 'not-started', text: 'Not started', size: 0, originalSize: null,
        },
        {
          name: 'in-progress', text: 'In progress', size: 0, originalSize: null,
        },
        {
          name: 'completed', text: 'Completed', size: 0, originalSize: null,
        },
      ],
    };
  },
  computed: {
    filters() {
      const technologies = this.data.technologies.reduce((p, t) => ({ ...p, [t.id]: { text: t.name } }), {});
      const topics = this.data.topics.reduce((p, t) => ({ ...p, [t.id]: { text: t.name } }), {});

      return {
        technology: {
          name: 'Technology',
          type: 'multiple',
          values: technologies,
          open: true,
        },
        topic: {
          name: 'Topics',
          type: 'multiple',
          values: topics,
          open: true,
        },
        type: {
          name: 'Project type',
          values: {
            practice: { text: 'Practice' },
            course: { text: 'Course' },
            track: { text: 'Career Track' },
            skill: { text: 'Skill Track' },
          },
          open: true,
        },
        difficulty: {
          name: 'Difficulty',
          values: {
            1: { text: 'Beginner' },
            2: { text: 'Intermediate' },
            3: { text: 'Advanced' },
          },
          open: true,
        },
        duration: {
          name: 'Duration',
          values: {
            1: { text: '< 3 hour' },
            2: { text: '3-10 hours' },
            3: { text: '> 10 hours' },
          },
          open: true,
        },
        ...!this.paidAccess ? {
          free: {
            name: 'Free',
            values: {
              1: { text: 'Free' },
              0: { text: 'Paid' },
            },
            open: true,
          },
        } : {},
      };
    },
    deSelected() {
      return this.deSelectedOption;
    },
    clearSearch() {
      return this.keyword;
    },
    filtersReset() {
      return this.resetFilters;
    },
  },
  methods: {
    async fetchProjects() {
      const { projects, paidAccess } = await this.$axios.$get(`/projects?page=${this.page}&tab=${this.currentTab}&q=${this.keyword}&${this.filter}`, { progress: false });

      this.projects = projects;
      this.paidAccess = paidAccess;
      this.searchLabels.results = projects.count;
      this.searchLabels.searchData = this.keyword;
    },
    close(label) {
      if (label.type === 'search') {
        this.keyword = '';
      } else {
        this.deSelectedOption = label;
      }
    },
    reset() {
      this.resetFilters = true;
    },
    async changeTab(tab) {
      this.currentTab = tab;
      this.page = 1;
      this.fetchProjects();
    },
    async filtrate(f) {
      setTimeout(() => {
        this.resetFilters = false;
      }, 1000);
      this.page = 1;
      this.filter = f.params;
      this.searchLabels.filtersData = f.filter;
      await this.fetchProjects();
    },
    setPage(page) {
      this.page = page;
      this.fetchProjects();
      let offset;
      if (this.$store.state.layout === 'mobile' && !this.$store.state.tablet) {
        offset = 330;
      } else if (this.$store.state.layout === 'mobile' && this.$store.state.tablet && this.$config.project === 'lms') {
        offset = 230;
      } else if (this.$store.state.layout === 'mobile' && this.$store.state.tablet && this.$config.project === 'site') {
        offset = 310;
      } else if (this.$config.project === 'lms') {
        offset = 250;
      } else {
        offset = 270;
      }
      window.scrollTo({
        top: offset,
        behavior: 'smooth',
      });
    },
    search(k) {
      this.keyword = k;
      this.fetchProjects();
    },
  },
};
</script>

<style lang="scss" scoped>
.projects-wrapper {
  background: var(--main-light-shade-1);

  &.site {
    .hero-section {
      min-height: 348px;
      padding-top: var(--spacing-80);
      margin-bottom: var(--spacing-80);

      .h3 {
        font-size: 40px;
        line-height: 110%;
      }
    }

    section {
      &.faq {
        margin-top: 130px;
        padding-bottom: 130px;
      }
    }

    .content {
      ::v-deep {
        .filters-accordion {
          .head,
          .filter-name {
            color: var(--main-dark);
            font-size: 16px;
            line-height: 110%;
            font-weight: 500;
          }

          .head {
            font-size: 21px;
          }
        }

        .search .results-wrapper .search-results {
          font-size: 18px;
          line-height: 110%;
        }
      }
    }

    @include bp($bp-laptop) {
      .hero-section {
        .h3 {
          font-size: 32px;
        }
      }
    }

    @include bp($bp-mobile) {
      .hero-section {
        min-height: 375px;
        padding: 68px 0 0;
      }

      .content {
        ::v-deep {
          .pagination {
            margin-bottom: var(--spacing-80);
          }
        }
      }

      section {
        &.faq {
          margin-top: var(--spacing-80);
          padding-bottom: var(--spacing-160);

          ::v-deep .h3 {
            font-size: 28px;
          }
        }
      }
    }
  }

  .hero-section {
    color: var(--main-light);
    background: linear-gradient(135deg, rgba(86, 191, 199, 0.2) 0.94%, rgba(86, 191, 199, 0) 100%), var(--main-dark);
    min-height: 212px;
    display: flex;
    align-items: center;
    margin-bottom: 64px;

    .h3 {
      margin-bottom: 0;
      line-height: 118%;
      font-weight: 600;
    }

    .description {
      padding-left: 0;
      line-height: 162%;
      color: var(--main-light-shade-4);
      margin-bottom: 0;
    }
  }

  section {
    &.faq {
      padding-bottom: 114px;
      margin-top: var(--spacing-80);

      a {
        color: var(--accent);
      }

      ::v-deep .projects .col-10 {
        padding-left: 0;

        .body-1 a {
          color: var(--accent);
        }
      }
    }
  }

  .content {
    .mobile-filter {
      display: none;
    }

    aside {
      order: 1;
    }

    .main {
      order: 2;
    }

    .types-tabs {
      order: 0;
      margin-bottom: 48px;

      .tabs {
        list-style: none;
        padding: 0;
        margin: 0;
        display: flex;
        align-items: center;

        li {
          margin: 0;
          position: relative;
          min-width: 170px;
          text-align: center;

          a {
            color: var(--main-light-shade-4) !important;
            border-bottom: 3px solid var(--main-light-shade-4);
            padding-bottom: var(--spacing-8);
            text-decoration: none;
            font-weight: bold;
            cursor: pointer;
            display: block;
          }

          &.active a {
            color: var(--accent) !important;
            border-bottom-color: var(--accent);
          }
        }
      }
    }

    ::v-deep {
      .filters-accordion {
        .head,
        .filter-name {
          color: var(--main-dark-light-3);
          font-size: 16px;
          line-height: 167%;
          font-weight: 400;
        }

        .head {
          margin-bottom: var(--spacing-30);
        }
      }

      .search {
        .results-wrapper .search-results {
          font-size: 16px;
          line-height: 167%;
        }

        .search-bar input {
          border-color: var(--main-light-shade-2);
        }
      }

      .pagination {
        margin: 0;
      }
    }
  }

  ::v-deep .faq {
    .h3 {
      font-size: 28px;
      line-height: 120%;
    }

    .body-1 {
      font-size: 16px;
      margin-top: 0;
    }
  }

  @include bp($bp-laptop) {
    h2,
    .h2 {
      font-size: 38px;
      line-height: 45px;
    }

    h3,
    .h3 {
      font-size: 32px;
      line-height: 40px;
    }

    h4,
    .h4 {
      font-size: 24px;
      line-height: 30px;
    }

    section {
      &.info {
        ol li .content .title {
          font-size: 18px;
          line-height: 139%;
        }
      }
    }

    ::v-deep .faq {
      .h3 {
        font-size: 24px;
      }
    }
  }

  @include bp($bp-phone) {
    &.site {
      .hero-section {
        .h3 {
          font-size: 28px;
          line-height: 110%;
        }

        .description {
          line-height: 150%;
        }
      }
    }

    .content {
      .types-tabs {
        margin-bottom: var(--spacing-20);

        .tabs {
          flex-direction: column;
          align-items: flex-start;

          li {
            width: 100%;

            a {
              text-align: left;
              border-bottom: 0;
              border-left: 3px solid var(--main-light-shade-4);
              padding: var(--spacing-12);
            }

            &.active a {
              color: var(--accent) !important;
              border-left-color: var(--accent);
            }
          }
        }
      }
    }
  }

  @include bp($bp-mobile) {
    .hero-section {
      padding: var(--spacing-40) 0;
      margin-bottom: var(--spacing-40) !important;

      .description {
        padding-right: 0;
      }

      h2 {
        flex-direction: column;
        align-items: flex-start;
      }
    }

    section {
      margin-top: 80px;

      &.info {
        margin-top: var(--spacing-40);
        margin-bottom: var(--spacing-50);

        .title {
          font-size: 24px;
        }

        ol li .content {
          .title {
            font-size: 18px;
            line-height: 139%;
          }

          .description {
            margin-bottom: var(--spacing-30);
          }
        }
      }

      &.faq {
        ::v-deep .h3 {
          font-size: 21px;
        }
      }

      > .container > h3 {
        font-size: 28px;
      }
    }

    .content {
      .desktop-filter {
        display: none;
      }

      .mobile-filter {
        display: block;
      }

      .row {
        margin-left: -12px;
        margin-right: -12px;
      }

      .types-tabs {
        padding-left: 15px;
      }

      .search .search-holder {
        display: flex;
        justify-content: space-between;

        .search-bar-wrapper {
          margin-right: var(--spacing-6);
        }
      }

      ::v-deep {
        .pagination {
          margin-top: var(--spacing-20);
        }
      }
    }
  }
}

.fa {
  .projects-wrapper {
    background: var(--main-light-shade-2);

    .hero-section {
      background: linear-gradient(179.58deg, rgba(3, 111, 253, 0.14) -0.05%, rgba(3, 111, 253, 0.05) 25.66%, rgba(1, 22, 51, 0) 99.77%), var(--main-dark);
    }

    .content {
      ::v-deep {
        .search .search-bar input {
          border-color: var(--main-light-shade-3);
        }

        .results-wrapper {
          .search-label {
            background: var(--main-light-shade-2-200);
          }
        }
      }
    }
  }

  section.introduction > .container > h3 {
    color: var(--accent-shade-1);
  }

  .projects-wrapper section.structure .tabs .tab {
    background: var(--main-light-shade-2-200);

    &.active {
      background: rgba(156, 163, 184, 0.5);
    }
  }
}
</style>
